import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const Privacy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title="Privacy Policy | ScopeTheMarket"
        description="ScopeTheMarket privacy policy. Learn how we collect, use, and protect your personal information on our stock market intelligence platform."
        canonical="https://scopethemarket.com/privacy"
        noIndex={false}
      />
      {/* Navigation */}
      <nav className="relative z-50 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="ScopeTheMarket" 
                className="h-9 w-auto"
                style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))' }}
              />
              <span className="text-xl font-semibold tracking-tight" style={{ color: '#F8FAFC' }}>
                ScopeTheMarket
              </span>
            </Link>
            
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium transition-colors hover:text-white"
                style={{ color: '#94A3B8' }}
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2.5 text-sm font-medium rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  color: 'white'
                }}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm transition-colors hover:text-white"
            style={{ color: '#64748B' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <h1 
            className="text-4xl font-bold mb-4"
            style={{ color: '#F8FAFC' }}
          >
            Privacy Policy
          </h1>
          <p className="mb-12" style={{ color: '#64748B' }}>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {/* Content */}
          <div className="space-y-10" style={{ color: '#94A3B8' }}>
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>1. Introduction</h2>
              <p className="leading-relaxed">
                ScopeTheMarket ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>2. Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                We collect information you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Account information (email address, password)</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Usage data and preferences</li>
                <li>Communications you send to us</li>
              </ul>
              <p className="leading-relaxed">
                We automatically collect certain information when you use the Service, including your IP address, 
                browser type, operating system, and usage patterns.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide, maintain, and improve the Service</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>4. Information Sharing</h2>
              <p className="leading-relaxed mb-4">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>With service providers who assist in our operations (e.g., Stripe for payments)</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights, privacy, safety, or property</li>
                <li>In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>5. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
                over the Internet or electronic storage is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>6. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>7. Your Rights</h2>
              <p className="leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate or incomplete data</li>
                <li>Deletion of your personal information</li>
                <li>Restriction of processing</li>
                <li>Data portability</li>
                <li>Objection to processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>8. Cookies and Tracking</h2>
              <p className="leading-relaxed">
                We use cookies and similar tracking technologies to collect information about your browsing activities. 
                You can set your browser to refuse all or some browser cookies, or to alert you when cookies are being sent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>9. Third-Party Links</h2>
              <p className="leading-relaxed">
                The Service may contain links to third-party websites. We are not responsible for the privacy practices 
                or content of these third-party sites. We encourage you to read the privacy policies of any third-party 
                sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>10. Children's Privacy</h2>
              <p className="leading-relaxed">
                The Service is not intended for children under 18 years of age. We do not knowingly collect personal 
                information from children under 18. If we learn we have collected personal information from a child 
                under 18, we will delete that information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>11. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
                new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>12. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us through our support channels.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6" style={{ borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-center" style={{ color: '#475569' }}>
            © {new Date().getFullYear()} ScopeTheMarket. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
